import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { defaultRange, num, rangeParams, usd, useLoad, useStaff, when } from "@/lib/staff";
import { Card, ErrorBox, PageHead, RangePicker, Skeleton, Stat, Table } from "@/components/staff/ui";
import { KindBadge, TrackingNote, modelColumns } from "@/components/staff/bits";

function RecentErrors({ rows }) {
  if (!rows.length) return <p className="text-sm text-neutral-500" data-testid="ov-errors-empty">No errors recorded.</p>;
  return (
    <ul className="space-y-2.5" data-testid="ov-recent-errors">
      {rows.map((e) => (
        <li key={e.error_id} className="text-sm">
          <div className="flex items-center gap-2"><KindBadge kind={e.kind} /><span className="text-xs text-neutral-500">{when(e.created_at)}</span></div>
          <p className="text-neutral-300 mt-1 line-clamp-2">{e.message}</p>
          {e.user_id && <Link to={`/staff/students/${e.user_id}`} className="text-xs text-sky-300 hover:underline">{e.user_email || "Open student"}</Link>}
        </li>
      ))}
    </ul>
  );
}

function OverviewBody({ d, withCost }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Students" value={num(d.students)} testid="ov-students" />
        <Stat label="New students in this range" value={num(d.new_students)} testid="ov-new-students" />
        <Stat label="Active today" value={num(d.active.today)} hint={`${num(d.active["7d"])} in 7 days · ${num(d.active["30d"])} in 30 days`} testid="ov-active" />
        <Stat label="Errors in this range" value={num(d.errors_in_range)} testid="ov-errors" />
        <Stat label="AI requests" value={num(d.ai.requests)} testid="ov-ai-requests" />
        <Stat label="Failed AI requests" value={num(d.ai.failed)} testid="ov-ai-failed" />
        <Stat label="Tokens (estimated)" value={num(d.ai.tokens)} testid="ov-ai-tokens" />
        {withCost && <Stat label="Estimated AI cost" value={usd(d.ai.est_cost)} hint="Our estimate, not a Stripe amount" testid="ov-ai-cost" />}
      </div>
      <div className="grid lg:grid-cols-5 gap-4 mt-4">
        <div className="lg:col-span-3">
          <Table testid="ov-models" columns={modelColumns(withCost)} rows={d.models} rowKey={(r) => r.model} empty="No AI requests in this range." maxH="max-h-[420px]" />
        </div>
        <Card title="Latest errors" className="lg:col-span-2" action={<Link to="/staff/errors" className="text-xs text-sky-300 hover:underline" data-testid="ov-errors-link">See all</Link>}>
          <RecentErrors rows={d.recent_errors} />
        </Card>
      </div>
      <TrackingNote since={d.tracking_since} />
    </>
  );
}

export default function OverviewPage() {
  const { can } = useStaff();
  const [range, setRange] = useState(() => defaultRange("7d"));
  const params = useMemo(() => rangeParams(range), [range]);
  const { data, error, loading, reload } = useLoad("/staff/overview", params);
  return (
    <div data-testid="staff-overview">
      <PageHead title="Overview" sub="How BrynAI is doing. Chat messages, files and AI answers are never shown in the staff area.">
        <RangePicker value={range} onChange={setRange} testid="ov-range" />
      </PageHead>
      {error && <div className="mb-4"><ErrorBox error={error} onRetry={reload} /></div>}
      {!data && loading && <Skeleton rows={6} />}
      {data && <OverviewBody d={data} withCost={can("billing.view")} />}
    </div>
  );
}
